import axios from 'axios';
import * as query from '@arcgis/core/rest/query';

// Utils
import { getLevelRank } from '../utils/admin2Data.utils';

// Configs
import { ADMIN_1_VECTOR_DATA_WASH_TYPE, DHS_API_MAP_SERVER_COUNTRIES_DATA_URL } from '../configs/api.configs';
import {
  INDICATOR_TYPE_WASH,
  IndicatorType,
  NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP,
  NO_IMPROVED_WATER_PREMISES_FIELD_VALUE,
} from '../configs/indicators.config';
import { DATA_VALUE_PRECISION } from '../configs/map.configs';
import { SurveySelectedType } from '../configs/types';

/**
 * Get DHS API survey data
 * @param {SurveySelectedType} surveySelected - Survey selected
 * @param {IndicatorType} indicatorSelected - Indicator selected
 * @param {string} returnFields - Return fields
 */
const getDhsApiSurveyData = async (
  surveySelected: SurveySelectedType,
  indicatorSelected: IndicatorType,
  returnFields: string
) => {
  const { DHSCC, year, SurveyType } = surveySelected;

  const requestConfig = {
    method: 'get',
    url: DHS_API_MAP_SERVER_COUNTRIES_DATA_URL,
    params: {
      breakdown: 'all',
      // indicatorIds: 'CN_NUTS_C_HA2',
      indicatorIds: indicatorSelected.value,
      // countryIds: 'BF',
      countryIds: DHSCC,
      // surveyIds: 'BF2021DHS',
      surveyIds: DHSCC + year + SurveyType,
      LevelRank: 1,
      lang: 'en',
      f: 'json',
      returnFields: returnFields,
    },
  };

  const { data } = await axios(requestConfig);

  return data;
};

/**
 * Process DHS API survey data grouped by region ID
 * @param {[]} dhsApiSurveyData - DHS API survey data
 */
const processDhsApiSurveyDataGroupedByRegionID = (dhsApiSurveyData: any) => {
  const surveyDataGroupedByRegionID: any = {};
  dhsApiSurveyData.Data.filter((item: any) => {
    return item.RegionId !== '' && item.LevelRank === 1;
  }).forEach((item: any) => {
    surveyDataGroupedByRegionID[item.RegionId] = {
      CountryName: item.CountryName,
      CharacteristicLabel: item.CharacteristicLabel,
      SurveyLabel: `${item.SurveyYearLabel} ${item.SurveyType}`,
      Value: item.Value,
    };
  });

  return surveyDataGroupedByRegionID;
};

function getQueryWhereValue(indicatorSelected: IndicatorType, surveySelected: SurveySelectedType) {
  const surveyId = `'${surveySelected.DHSCC}${surveySelected.year}${surveySelected.SurveyType}'`;

  const countryCode = surveySelected.DHSCC;
  const levelRank = getLevelRank(countryCode);

  if (indicatorSelected.value === NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP) {
    // TODO: This is a temporary fix to handle the fact that the indicator value is not the same as the field name in the GIS REST API
    return `(IndicatorId = '${NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP}' AND SurveyId = ${surveyId} OR IndicatorId = '${NO_IMPROVED_WATER_PREMISES_FIELD_VALUE}' AND SurveyId = ${surveyId}) AND LevelRank = ${levelRank}`;
  } else {
    return `IndicatorId = '${indicatorSelected.value}' AND SurveyId = ${surveyId} AND LevelRank = ${levelRank}`;
  }
}

/**
 * Get GIS REST API survey data
 * @param {IndicatorType} indicatorSelected - Indicator selected
 * @param {SurveySelectedType} surveySelected - Survey selected
 * @returns {Promise<any[]>} - GIS REST API survey data
 */
export const getGisRestApiSurveyData = async (
  indicatorSelected: IndicatorType,
  surveySelected: SurveySelectedType
): Promise<any[]> => {
  const outFieldsList = ['adm0_name', 'adm1_name', 'estimates', 'year', 'SurveyType', 'RegionID', 'LevelRank'];

  const queryParams = {
    where: getQueryWhereValue(indicatorSelected, surveySelected),
    outFields: [outFieldsList.join()],
  };

  const results: __esri.FeatureSet = await query.executeQueryJSON(ADMIN_1_VECTOR_DATA_WASH_TYPE, queryParams);
  const resultsAttributes = results.features.map((feature: any) => {
    return feature.attributes;
  });

  return resultsAttributes;
};

/**
 * Process GIS REST API survey data grouped by region ID
 * @param {[]} resultsAttributes - Results attributes
 * @returns {Object} - Survey data grouped by region ID
 */
const processGisRestApiSurveyDataGroupedByRegionID = (resultsAttributes: any[]) => {
  const surveyDataGroupedByRegionID: any = {};
  resultsAttributes.forEach((item: any) => {
    const attributes = {
      CountryName: item.adm0_name,
      CharacteristicLabel: item.adm1_name,
      SurveyLabel: `${item.year} ${item.SurveyType}`,
      Value: item.estimates.toFixed(DATA_VALUE_PRECISION),
    };

    surveyDataGroupedByRegionID[item.RegionID] = attributes;
  });

  return surveyDataGroupedByRegionID;
};

export const getSurveyData = async (surveySelected: SurveySelectedType, indicatorSelected: IndicatorType) => {
  try {
    if (indicatorSelected.type !== INDICATOR_TYPE_WASH) {
      const returnFields = 'RegionId,LevelRank,CountryName,CharacteristicLabel,SurveyYearLabel,SurveyType,Value';
      const dhsApiSurveyData = await getDhsApiSurveyData(surveySelected, indicatorSelected, returnFields);
      const surveyDataGroupedByRegionID = processDhsApiSurveyDataGroupedByRegionID(dhsApiSurveyData);

      return surveyDataGroupedByRegionID;
    } else {
      const resultsAttributes = await getGisRestApiSurveyData(indicatorSelected, surveySelected);
      const surveyDataGroupedByRegionID = processGisRestApiSurveyDataGroupedByRegionID(resultsAttributes);

      return surveyDataGroupedByRegionID;
    }
  } catch (error) {
    console.error(error);
    return [];
  }
};
